import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useScrollToTop from '../hooks/useScrollToTop';
import { useSEO } from '../hooks/useSEO';
import { useTranslation } from '../hooks/useTranslation';
import { useBlogPosts } from '../lib/hub';

const formatDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const PostMeta = ({ post }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
    {post.category && <span className="text-cyan-400">{post.category}</span>}
    {formatDate(post.publishedAt || post.updatedAt) && <span>{formatDate(post.publishedAt || post.updatedAt)}</span>}
    {post.readingTime && <span>{post.readingTime}</span>}
  </div>
);

const Cover = ({ post, className }) =>
  post.coverImage && post.coverImage.url ? (
    <img
      src={post.coverImage.url}
      alt={post.coverImage.alt || ''}
      loading="lazy"
      className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${className || ''}`}
    />
  ) : (
    <div className={`relative w-full h-full bg-slate-900 bg-grid ${className || ''}`}>
      <span className="absolute left-5 bottom-4 font-display font-bold text-5xl text-outline select-none" aria-hidden="true">
        {(post.category || post.title || 'IP9').slice(0, 2)}
      </span>
    </div>
  );

const FeaturedPost = ({ post }) => (
  <Link to={post.path} className="group grid lg:grid-cols-2 border border-slate-800 hover:border-cyan-500/60 transition-colors">
    <div className="aspect-[16/9] lg:aspect-auto lg:min-h-[22rem] overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-800">
      <Cover post={post} />
    </div>
    <div className="p-8 md:p-10 flex flex-col">
      <PostMeta post={post} />
      <h2 className="mt-5 font-display font-bold text-3xl md:text-4xl text-slate-50 leading-tight group-hover:text-cyan-400 transition-colors">
        {post.title}
      </h2>
      {post.excerpt && <p className="mt-4 text-slate-400 leading-relaxed line-clamp-4">{post.excerpt}</p>}
      <span className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400">
        Read article <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

const PostCard = ({ post }) => (
  <Link to={post.path} className="group flex flex-col border border-slate-800 hover:border-cyan-500/60 bg-slate-950 transition-colors">
    <div className="aspect-[16/9] overflow-hidden border-b border-slate-800">
      <Cover post={post} />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <PostMeta post={post} />
      <h3 className="mt-3 font-display font-semibold text-xl text-slate-50 leading-snug group-hover:text-cyan-400 transition-colors">
        {post.title}
      </h3>
      {post.excerpt && <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{post.excerpt}</p>}
      <ArrowRight className="mt-auto pt-5 w-9 h-9 text-slate-600 group-hover:text-cyan-400 transition-colors" />
    </div>
  </Link>
);

const BlogIndexPage = () => {
  useSEO('blog');
  useScrollToTop();
  const { t } = useTranslation();
  const { items: posts, loading, error } = useBlogPosts();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const categories = useMemo(() => [...new Set(posts.map((p) => p.category).filter(Boolean))].sort(), [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (!q) return true;
      return [p.title, p.excerpt, ...(p.tags || [])].filter(Boolean).some((s) => s.toLowerCase().includes(q));
    });
  }, [posts, query, category]);

  const browsing = !query && category === 'all';
  const [featured, ...rest] = filtered;
  const grid = browsing ? rest : filtered;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main>
        <section className="grain relative overflow-hidden border-b border-slate-800">
          <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-14 md:pt-28">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-400 mb-5">{t('seo.blog.title')}</p>
            <h1 className="font-display font-bold text-5xl md:text-6xl text-slate-50 max-w-3xl leading-[0.98]">
              Notes from the <span className="text-cyan-500">proxy network</span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-2xl">{t('seo.blog.description')}</p>

            {posts.length > 0 && (
              <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4">
                <label className="relative w-full md:max-w-sm">
                  <span className="sr-only">Search articles</span>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search articles"
                    className="w-full bg-slate-900 border border-slate-700 focus:border-cyan-500 outline-none pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder-slate-500"
                  />
                </label>
                {categories.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {['all', ...categories].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCategory(c)}
                        className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] border transition-colors ${
                          category === c ? 'bg-cyan-500 border-cyan-500 text-slate-950' : 'border-slate-700 text-slate-400 hover:text-slate-100 hover:border-slate-500'
                        }`}
                      >
                        {c === 'all' ? `All (${posts.length})` : c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-px bg-slate-800 border border-slate-800" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-slate-950 p-6 space-y-4">
                  <div className="skeleton aspect-[16/9]" />
                  <div className="skeleton h-4 w-1/3" />
                  <div className="skeleton h-6 w-5/6" />
                </div>
              ))}
            </div>
          ) : error || posts.length === 0 ? (
            <div className="border border-slate-800 p-10 md:p-14 max-w-3xl">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-3">
                {error ? 'Temporarily unavailable' : 'First articles coming soon'}
              </p>
              <h2 className="font-display font-bold text-3xl text-slate-50 mb-4">
                {error ? 'We couldn\u2019t load the blog right now.' : 'Nothing published yet.'}
              </h2>
              <p className="text-slate-400 mb-8">
                In the meantime, our integration tutorials show how to use IP9 proxies with Python, Node.js, Selenium, Scrapy and more.
              </p>
              <Link to="/integrations" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-5 py-3 text-sm">
                Browse integration tutorials <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-slate-800 p-10 text-center">
              <p className="text-slate-300 mb-5">No articles match your search.</p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setCategory('all');
                }}
                className="border border-slate-700 hover:border-slate-500 text-slate-200 px-4 py-2 text-sm"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              {browsing && featured && <FeaturedPost post={featured} />}
              {grid.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {grid.map((post) => (
                    <PostCard key={post.path} post={post} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogIndexPage;
